// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@rpsc/App/Components/Common/FancyScroll";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import StyledBox from "@rpsc/XDL/Components/Common/Box";
import IconDocument from "@rpsc/XDL/Icons/IconDocument";
import IconExcel from "@rpsc/XDL/Icons/IconExcel";
import IconPDF from "@rpsc/XDL/Icons/IconPDF";
import IconWord from "@rpsc/XDL/Icons/IconWord";
import React, { createRef, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

interface Props extends WidgetProps {
    showTitle?: boolean;
}

type OwnProps = Props & HasProduct;
// </XNG-Change>

export interface ProductDetailsDocumentsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    // XNG-Change
    title?: TypographyPresentationProps;
    documents?: InjectableCss;
    showMoreLink?: LinkPresentationProps;
    showLessLink?: LinkPresentationProps;
}

export const documentsStyles: ProductDetailsDocumentsStyles = {
    wrapper: {
        css: css`
            padding: 10px 15px 30px;
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
};

const styles = documentsStyles;

const getIcon = (name: string): React.ComponentType<{}> => {
    const files = [
        { type: "\\.pdf", icon: IconPDF },
        { type: "\\.xls|\\.xlsx", icon: IconExcel },
        { type: "\\.doc|\\.docx", icon: IconWord },
    ];
    for (const _file of files) {
        if (name.match(new RegExp(_file.type, "gi"))) {
            return _file.icon;
        }
    }
    return IconDocument;
};

const ProductDetailsDocuments: React.FC<OwnProps> = ({
    // XNG-Change: rename documents, extract id, add title and fields.
    product: { documents: productDocuments, id: productId },
    showTitle = true,
}) => {
    // XNG-Change
    const [isExpanded, setIsExpanded] = useState(false);
    const documents = productDocuments;

    const parentRef = createRef<HTMLDivElement>();
    const wrapperRef = createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    useEffect(() => {
        setTimeout(() => {
            fancyScrollRef.current?.updateScrollable();
        }, 1000);
    }, []);

    if (!documents || documents.length === 0) {
        return null;
    }

    const visibleDocuments = isExpanded ? documents : documents.slice(0, 5);
    /* XNG-Change: Use StyledBox, show title, change icon */
    return (
        <StyledBox {...styles.wrapper} ref={wrapperRef}>
            {showTitle && (
                <StyledWrapper {...styles.documentGridItem}>
                    <Typography {...styles.title}>{translate("Documentation")}</Typography>
                </StyledWrapper>
            )}
            <FancyScroll
                refElement={parentRef}
                // paddingRight={8}
                // contentPaddingRight={0}
                // wrapperRef={wrapperRef}
                shadowHeight="8px"
                ref={fancyScrollRef}
            >
                <StyledWrapper {...styles.documents} ref={parentRef}>
                    {visibleDocuments.map(document => (
                        <StyledWrapper key={document.id.toString()} {...styles.documentGridItem}>
                            <Link
                                href={document.filePath}
                                target="_blank"
                                {...styles.documentLink}
                                data-test-selector={`productDetails_${document.id}`}
                                icon={{
                                    iconProps: {
                                        src: getIcon(document.name),
                                    },
                                    position: "left",
                                }}
                                aria-label={document.description || document.name}
                            >
                                {/* <IconMemo {...styles.documentIcon} /> */}
                                {document.description || document.name}
                            </Link>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </FancyScroll>
            {documents.length > 5 &&
                (isExpanded ? (
                    <Link
                        onClick={() => setIsExpanded(e => !e)}
                        {...styles.showLessLink}
                        aria-label={translate("Show Less")}
                    >
                        {translate("Show Less")}
                    </Link>
                ) : (
                    <Link
                        onClick={() => setIsExpanded(e => !e)}
                        {...styles.showMoreLink}
                        aria-label={translate("Show More")}
                    >
                        {translate("Show More")}
                    </Link>
                ))}
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDocuments),
    definition: {
        displayName: "Documents",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
